import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useAuth } from "../../context/authContext";
import { createBanner, deleteBanner, getBanners, updateBanner } from "../../services/bannerService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getUserAllocations } from "../../services/manageSitesService";
import { formatDateTime } from "../../utilities/globalServiceUtils";
import Datagrid from "../Datagrid/Datagrid";
import DatagridLoading from "../DatagridLoading/DatagridLoading";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import UserName from "../UserName/UserName";

const SEVERITY_OPTIONS = ["info", "success", "warning", "error"];
const SEVERITY_META = {
	info: { label: "Information", color: "#0288d1" },
	success: { label: "Success", color: "#2e7d32" },
	warning: { label: "Warning", color: "#ed6c02" },
	error: { label: "Error", color: "#d32f2f" },
};

const renderSeverityOption = (severity) => {
	const meta = SEVERITY_META[severity] || { label: severity, color: "#64748b" };
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<Box
				component="span"
				sx={{
					width: 10,
					height: 10,
					borderRadius: "50%",
					bgcolor: meta.color,
					flexShrink: 0,
				}}
			/>
			<Typography variant="body2">{meta.label}</Typography>
		</Box>
	);
};

const emptyForm = {
	title: "",
	message: "",
	legal_entities: ["ALL"],
	start_date: "",
	end_date: "",
	severity: "info",
	cta_label: "",
	cta_url: "",
	is_active: true,
	dismissible: true,
};

const BANNER_REFRESH_EVENT = "banner-refresh";

const BANNER_GRID_SX = {
	"& .MuiDataGrid-cell": {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
		textAlign: "left",
	},
	"& .MuiDataGrid-columnHeaderTitleContainer": {
		justifyContent: "flex-start",
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		textAlign: "left",
	},
};

const MAX_BANNER_TITLE_LENGTH = 100;
const MAX_BANNER_CTA_LABEL_LENGTH = 50;
const LEGAL_ENTITY_LABELS = {
	ALL: "All Legal Entities",
};

const getLegalEntityLabel = (entity) =>
	LEGAL_ENTITY_LABELS[String(entity || "").trim().toUpperCase()] || entity;

const toLegalEntityOption = (entity) => ({
	value: entity,
	label: getLegalEntityLabel(entity),
});

const getLegalEntitySelectionLabel = (selected = []) => {
	if (selected.some((option) => option?.value === "ALL")) {
		return "All Legal Entities";
	}

	return selected
		.map((option) => option?.label || option?.value)
		.filter(Boolean)
		.join(", ");
};

const getLocalDateInput = (value = new Date()) => {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const normalizeDateInput = (value) => (value ? String(value).slice(0, 10) : "");
const getSiteLegalEntity = (site = {}) =>
	String(site?.site_legal_entity || site?.legal_entity || site?.resolved_legal_entity || "AUSA").trim().toUpperCase();
const siteHasValidLegalEntity = (site = {}) =>
	site?.has_valid_legal_entity === false ? false : Boolean(getSiteLegalEntity(site));
const toDayjsDate = (value) => {
	const normalized = normalizeDateInput(value);
	return normalized ? dayjs(normalized) : null;
};
const formatDateDisplay = (value, fallback = "N/A") => {
	const normalized = normalizeDateInput(value);
	if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return fallback;
	return `${normalized.slice(5, 7)}-${normalized.slice(8, 10)}-${normalized.slice(0, 4)}`;
};
const escapeHtml = (value = "") =>
	String(value)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
const isValidFourDigitYearDate = (value) => {
	if (!value) return true;
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
	const year = Number(value.slice(0, 4));
	return year >= 1000 && year <= 9999;
};
const isValidCtaUrl = (value) => {
	const url = String(value || "").trim();
	if (!url) return true;
	if (url.startsWith("/") && !url.startsWith("//")) return true;
	try {
		const parsedUrl = new URL(url);
		return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
	} catch (_error) {
		return false;
	}
};

const normalizeList = (payload) => {
	if (Array.isArray(payload?.data?.banners)) return payload.data.banners;
	if (Array.isArray(payload?.banners)) return payload.banners;
	return [];
};

const DETAIL_MESSAGE_PREVIEW_LENGTH = 420;
const DETAIL_TITLE_PREVIEW_LENGTH = 90;
const shouldCollapseDetailMessage = (value) =>
	String(value || "").trim().length > DETAIL_MESSAGE_PREVIEW_LENGTH;
const getCollapsedDetailMessage = (value) => {
	const normalized = String(value || "").trim();
	if (normalized.length <= DETAIL_MESSAGE_PREVIEW_LENGTH) return normalized;
	return `${normalized.slice(0, DETAIL_MESSAGE_PREVIEW_LENGTH).trimEnd()}...`;
};
const shouldCollapseDetailTitle = (value) =>
	String(value || "").trim().length > DETAIL_TITLE_PREVIEW_LENGTH;
const getCollapsedDetailTitle = (value) => {
	const normalized = String(value || "").trim();
	if (normalized.length <= DETAIL_TITLE_PREVIEW_LENGTH) return normalized;
	return `${normalized.slice(0, DETAIL_TITLE_PREVIEW_LENGTH).trimEnd()}...`;
};

const ManageBanners = () => {
	const { user, hasPermission } = useAuth();
	const [banners, setBanners] = useState([]);
	const [form, setForm] = useState(emptyForm);
	const [editingId, setEditingId] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [loading, setLoading] = useState(true);
	const [actorScopeLoading, setActorScopeLoading] = useState(true);
	const [bannerSitesLoading, setBannerSitesLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [fieldErrors, setFieldErrors] = useState({});
	const [bannerSites, setBannerSites] = useState([]);
	const [legalEntityInput, setLegalEntityInput] = useState("");
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	const [detailBanner, setDetailBanner] = useState(null);
	const [detailMessageExpanded, setDetailMessageExpanded] = useState(false);
	const [detailTitleExpanded, setDetailTitleExpanded] = useState(false);
	const containerRef = useRef(null);
	const formRef = useRef(null);
	const todayDate = getLocalDateInput();
	const [pendingScrollBannerId, setPendingScrollBannerId] = useState(null);
	const isSuperAdmin = Number(user?.user_id) === 1;
	const actorRoleType = String(
		user?.role?.role_type ||
		user?.role?.role_name ||
		user?.user_role_name ||
		user?.user_role ||
		""
	)
		.trim()
		.toLowerCase();
	const directActorSiteIds = useMemo(
		() =>
			Array.from(
				new Set(
					(Array.isArray(user?.allocations) ? user.allocations : [])
						.map((allocation) => Number(allocation?.site_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			),
		[user?.allocations]
	);
	const effectiveActorSiteIds = useMemo(
		() => (directActorSiteIds.length > 0 ? directActorSiteIds : fallbackActorSiteIds),
		[directActorSiteIds, fallbackActorSiteIds]
	);
	const actorHasAllSiteVisibility = useMemo(
		() =>
			isSuperAdmin ||
			((actorRoleType === "admin" || hasPermission("user_management.manage_all_sites")) &&
				effectiveActorSiteIds.length === 0),
		[actorRoleType, effectiveActorSiteIds.length, hasPermission, isSuperAdmin]
	);
	const legalEntityOptions = useMemo(() => {
		const entities = Array.from(
			new Set(
				bannerSites
					.map(getSiteLegalEntity)
					.filter(Boolean)
			)
		).sort();
		if (actorHasAllSiteVisibility) return ["ALL", ...entities];
		return entities;
	}, [actorHasAllSiteVisibility, bannerSites]);
	const visibleBanners = useMemo(() => {
		if (actorHasAllSiteVisibility) return banners;
		const allowedEntities = new Set(legalEntityOptions.filter((entity) => entity !== "ALL"));
		return banners.filter((banner) => {
			const entities = Array.isArray(banner?.legal_entities) && banner.legal_entities.length
				? banner.legal_entities.map((entity) => String(entity || "").trim().toUpperCase())
				: ["ALL"];
			if (entities.includes("ALL")) return false;
			return entities.length > 0 && entities.every((entity) => allowedEntities.has(entity));
		});
	}, [actorHasAllSiteVisibility, banners, legalEntityOptions]);
	const canCreate = isSuperAdmin || hasPermission("banner_management.create_banners");
	const canEdit = isSuperAdmin || hasPermission("banner_management.edit_banners");
	const canDelete = isSuperAdmin || hasPermission("banner_management.delete_banners");
	const getSavedBannerIdFromResponse = (response, fallbackId = null) => {
		return (
			response?.data?.banner?.id ||
			response?.banner?.id ||
			fallbackId ||
			null
		);
	};
	useEffect(() => {
		if (!pendingScrollBannerId) return;
		if (!Array.isArray(visibleBanners) || visibleBanners.length === 0) return;

		const existsInCurrentGrid = visibleBanners.some(
			(banner) => String(banner?.id) === String(pendingScrollBannerId)
		);

		if (!existsInCurrentGrid) return;

		const timer = window.setTimeout(() => {
			const row = document.querySelector(`[data-id="${pendingScrollBannerId}"]`);

			if (row) {
				row.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});

				row.classList.add("recently-saved-banner-row");

				window.setTimeout(() => {
					row.classList.remove("recently-saved-banner-row");
				}, 2500);
			} else {
				containerRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
			}

			setPendingScrollBannerId(null);
		}, 300);

		return () => window.clearTimeout(timer);
	}, [pendingScrollBannerId, visibleBanners]);
	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (mounted) setActorScopeLoading(true);
			if (isSuperAdmin || directActorSiteIds.length > 0 || !user?.user_id) {
				if (mounted) setFallbackActorSiteIds([]);
				if (mounted) setActorScopeLoading(false);
				return;
			}
			try {
				const response = await getUserAllocations(user.user_id);
				const allocations = Array.isArray(response?.data?.allocations) ? response.data.allocations : [];
				const nextIds = Array.from(
					new Set(
						allocations
							.map((allocation) => Number(allocation?.site_id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				);
				if (mounted) setFallbackActorSiteIds(nextIds);
			} catch (_error) {
				if (mounted) setFallbackActorSiteIds([]);
			} finally {
				if (mounted) setActorScopeLoading(false);
			}
		};
		loadFallbackActorScope();
		return () => {
			mounted = false;
		};
	}, [directActorSiteIds, isSuperAdmin, user?.user_id]);

	useEffect(() => {
		let mounted = true;
		const loadBannerSites = async () => {
			if (mounted) setBannerSitesLoading(true);
			try {
				const response = await getAllCustomerSites();
				const allSites = Array.isArray(response?.data) ? response.data : [];
				const visibleSites = actorHasAllSiteVisibility
					? allSites
					: allSites.filter((site) => effectiveActorSiteIds.includes(Number(site.site_id)));
				if (mounted) setBannerSites(visibleSites.filter(siteHasValidLegalEntity));
			} catch (_error) {
				if (mounted) setBannerSites([]);
			} finally {
				if (mounted) setBannerSitesLoading(false);
			}
		};
		loadBannerSites();
		return () => {
			mounted = false;
		};
	}, [actorHasAllSiteVisibility, effectiveActorSiteIds]);

	const gridLoading = loading || actorScopeLoading || (!actorHasAllSiteVisibility && bannerSitesLoading);

	const loadBanners = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const response = await getBanners();
			if (response?.status === false) {
				setError(response?.message || "Unable to fetch banners.");
				setBanners([]);
				return;
			}
			setBanners(normalizeList(response));
		} catch (err) {
			setError(err?.message || "Unable to fetch banners.");
			setBanners([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadBanners();
	}, [loadBanners]);

	useEffect(() => {
		if (!showForm) return;
		let frameOne = 0;
		let frameTwo = 0;
		frameOne = window.requestAnimationFrame(() => {
			containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			frameTwo = window.requestAnimationFrame(() => {
				formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			});
		});
		return () => {
			window.cancelAnimationFrame(frameOne);
			window.cancelAnimationFrame(frameTwo);
		};
	}, [showForm, editingId]);

	const resetForm = () => {
		setForm({
			...emptyForm,
			legal_entities: [],
		});
		setLegalEntityInput("");
		setEditingId(null);
		setShowForm(false);
		setError("");
		setFieldErrors({});
	};

	const handleCreate = () => {
		setDetailBanner(null);
		setForm({
			...emptyForm,
			legal_entities: [],
		});
		setLegalEntityInput("");
		setEditingId(null);
		setShowForm(true);
		setError("");
		setFieldErrors({});
	};

	const updateForm = (field, value) => {
		if (field === "title" && String(value || "").length > MAX_BANNER_TITLE_LENGTH) {
			setFieldErrors((prev) => ({
				...prev,
				title: `Title should not exceed ${MAX_BANNER_TITLE_LENGTH} characters.`,
			}));
			return;
		}

		if (field === "cta_label" && String(value || "").length > MAX_BANNER_CTA_LABEL_LENGTH) {
			setFieldErrors((prev) => ({
				...prev,
				cta_label: `CTA Label should not exceed ${MAX_BANNER_CTA_LABEL_LENGTH} characters.`,
			}));
			return;
		}

		setForm((prev) => ({ ...prev, [field]: value }));
		setFieldErrors((prev) => {
			if (!prev[field] && !(field === "start_date" && prev.end_date)) return prev;
			const next = { ...prev };
			delete next[field];
			if (field === "start_date") delete next.end_date;
			return next;
		});
	};

	const handleLegalEntitiesChange = (_event, value = []) => {
		let next = value.map((option) => option?.value).filter(Boolean);
		if (actorHasAllSiteVisibility && next.includes("ALL") && !form.legal_entities.includes("ALL")) {
			next = ["ALL"];
		} else if (actorHasAllSiteVisibility && next.includes("ALL") && next.length > 1) {
			next = next.filter((entry) => entry !== "ALL");
		}
		setLegalEntityInput("");
		setForm((prev) => ({ ...prev, legal_entities: next }));
		setFieldErrors((prev) => {
			if (!prev.legal_entities) return prev;
			const nextErrors = { ...prev };
			delete nextErrors.legal_entities;
			return nextErrors;
		});
	};

	const handleEdit = (banner) => {
		setDetailBanner(null);
		setEditingId(banner.id);
		setShowForm(true);
		setLegalEntityInput("");
		setForm({
			title: banner.title || "",
			message: banner.message || "",
			legal_entities: Array.isArray(banner.legal_entities) && banner.legal_entities.length
				? banner.legal_entities.filter((entity) =>
					actorHasAllSiteVisibility ||
					legalEntityOptions.includes(String(entity || "").trim().toUpperCase())
				)
				: actorHasAllSiteVisibility ? ["ALL"] : [],
			start_date: normalizeDateInput(banner.start_date),
			end_date: normalizeDateInput(banner.end_date),
			severity: banner.severity || "info",
			cta_label: banner.cta_label || "",
			cta_url: banner.cta_url || "",
			is_active: banner.is_active !== false,
			dismissible: banner.dismissible !== false,
		});
	};

	const validateForm = () => {
		const errors = {};

		const title = String(form.title || "").trim();
		const message = String(form.message || "").trim();
		const ctaLabel = String(form.cta_label || "").trim();
		const ctaUrl = String(form.cta_url || "").trim();

		if (!title) {
			errors.title = "Title is required.";
		} else if (title.length > MAX_BANNER_TITLE_LENGTH) {
			errors.title = `Title should not exceed ${MAX_BANNER_TITLE_LENGTH} characters.`;
		}

		if (!message) {
			errors.message = "Message is required.";
		}

		if (ctaLabel.length > MAX_BANNER_CTA_LABEL_LENGTH) {
			errors.cta_label = `CTA Label should not exceed ${MAX_BANNER_CTA_LABEL_LENGTH} characters.`;
		}

		if (!(Array.isArray(form.legal_entities) && form.legal_entities.length > 0)) {
			errors.legal_entities = "Legal entity is required.";
		}

		if (!isValidCtaUrl(ctaUrl)) {
			errors.cta_url = "Enter a valid CTA URL. Use https://example.com or /page.";
		}

		if (!form.start_date) {
			errors.start_date = "Start date is required.";
		} else if (!isValidFourDigitYearDate(form.start_date)) {
			errors.start_date = "Start date must use a 4-digit year.";
		} else if (form.start_date < todayDate) {
			errors.start_date = "Start date cannot be earlier than today.";
		}

		if (!form.end_date) {
			errors.end_date = "End date is required.";
		} else if (!isValidFourDigitYearDate(form.end_date)) {
			errors.end_date = "End date must use a 4-digit year.";
		} else if (form.end_date < todayDate) {
			errors.end_date = "End date cannot be earlier than today.";
		}

		if (
			form.start_date &&
			form.end_date &&
			isValidFourDigitYearDate(form.start_date) &&
			isValidFourDigitYearDate(form.end_date) &&
			form.end_date < form.start_date
		) {
			errors.end_date = "End date must be after start date.";
		}

		return errors;
	};

	const handleSubmit = async () => {
		const validationErrors = validateForm();
		if (Object.keys(validationErrors).length > 0) {
			setFieldErrors(validationErrors);
			return;
		}
		setSaving(true);
		setError("");
		setFieldErrors({});
		const payload = { ...form };
		try {
			const response = editingId
				? await updateBanner(editingId, payload)
				: await createBanner(payload);
			if (response?.status === false) {
				setError(response?.message || "Unable to save banner.");
				return;
			}
			const nextBanners = normalizeList(response);
			const savedBannerId = getSavedBannerIdFromResponse(response, editingId);

			setBanners(nextBanners);
			setPendingScrollBannerId(savedBannerId);
			window.dispatchEvent(new Event(BANNER_REFRESH_EVENT));
			resetForm();
		} catch (err) {
			setError(err?.message || "Unable to save banner.");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (banner) => {
		const result = await Swal.fire({
			icon: "warning",
			title: "Delete banner?",
			html: `<div style="max-width:100%;white-space:normal;word-break:break-word;overflow-wrap:anywhere;text-align:center;">${escapeHtml(
				banner?.title || "This banner will be removed."
			)}</div>`,
			showCancelButton: true,
			confirmButtonText: "Delete",
			cancelButtonText: "Cancel",
			confirmButtonColor: "#d33",
		});
		if (!result.isConfirmed) return;
		setSaving(true);
		setError("");
		try {
			const response = await deleteBanner(banner.id);
			if (response?.status === false) {
				setError(response?.message || "Unable to delete banner.");
				return;
			}
			setBanners(normalizeList(response));
			window.dispatchEvent(new Event(BANNER_REFRESH_EVENT));
			if (detailBanner?.id === banner.id) closeDetail();
			if (editingId === banner.id) resetForm();
		} catch (err) {
			setError(err?.message || "Unable to delete banner.");
		} finally {
			setSaving(false);
		}
	};

	const openDetail = (banner) => {
		if (!banner) return;
		setDetailTitleExpanded(false);
		setDetailMessageExpanded(false);
		setDetailBanner(banner);
	};

	const closeDetail = () => {
		setDetailTitleExpanded(false);
		setDetailMessageExpanded(false);
		setDetailBanner(null);
	};

	const formDisabled = saving || (editingId ? !canEdit : !canCreate);
	const bannerMinDate = todayDate;
	const columns = [
		{
			field: "title",
			headerName: "Title",
			flex: 1.5,
			minWidth: 220,
			renderCell: (params) => (
				<Box sx={{ minWidth: 0, py: 1 }}>
					<Typography variant="body2" fontWeight={600} noWrap>{params.row?.title}</Typography>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						{params.row?.message}
					</Typography>
				</Box>
			),
		},
		{
			field: "legal_entities_display",
			headerName: "Legal Entity",
			flex: 1,
			minWidth: 160,
			valueGetter: (_value, row) => (row?.legal_entities || ["ALL"]).join(", "),
			renderCell: (params) => {
				const entities = params.row?.legal_entities?.length ? params.row.legal_entities : ["ALL"];
				return (
					<Tooltip title={entities.map(getLegalEntityLabel).join(", ")} arrow>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 1 }}>
							{entities.map((entity) => (
								<Chip key={entity} label={getLegalEntityLabel(entity)} size="small" />
							))}
						</Box>
					</Tooltip>
				);
			},
		},
		{
			field: "severity",
			headerName: "Severity",
			flex: 0.9,
			minWidth: 140,
			renderCell: (params) => (
				<Box sx={{ py: 1, width: "100%" }}>
					{renderSeverityOption(params.row?.severity || "info")}
				</Box>
			),
		},
		{
			field: "cta_display",
			headerName: "CTA",
			flex: 1.5,
			minWidth: 200,
			sortable: false,
			renderCell: (params) => (
				<Box sx={{ minWidth: 0, py: 1 }}>
					<Typography variant="body2" fontWeight={600} noWrap>
						{params.row?.cta_label || "N/A"}
					</Typography>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{
							display: "block",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{params.row?.cta_url || "-"}
					</Typography>
				</Box>
			),
		},
		{
			field: "start_date",
			headerName: "Start Date",
			flex: 1,
			minWidth: 120,
			valueGetter: (_value, row) => formatDateDisplay(row?.start_date),
		},
		{
			field: "end_date",
			headerName: "End Date",
			flex: 1,
			minWidth: 120,
			valueGetter: (_value, row) => formatDateDisplay(row?.end_date),
		},
		{
			field: "is_active",
			headerName: "Status",
			width: 80,
			renderCell: (params) => (
				<Chip
					label={params.row?.is_active === false ? "Inactive" : "Active"}
					size="small"
					color={params.row?.is_active === false ? "default" : "success"}
				/>
			),
		},
		{
			field: "updated_at",
			headerName: "Last Modified On",
			flex: 1,
			minWidth: 190,
			valueFormatter: (value) => formatDateTime(value) || "-",
		},
		{
			field: "updated_by_display_name",
			headerName: "Last Modified By",
			flex: 1.2,
			minWidth: 190,
			renderCell: (params) => (
				<Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", height: "100%", width: "100%" }}>
					<UserName size={24} name={params.row?.updated_by_display_name || "N/A"} />
				</Box>
			),
		},
		{
			field: "actions",
			headerName: "Actions",
			width: 130,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<Box sx={{ display: "flex", gap: 0.5 }}>
					<Tooltip title="Edit">
						<span>
							<IconButton
								size="small"
								onClick={(event) => {
									event.stopPropagation();
									handleEdit(params.row);
								}}
								disabled={!canEdit || saving}
							>
								<EditIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Delete">
						<span>
							<IconButton
								size="small"
								color="error"
								onClick={(event) => {
									event.stopPropagation();
									handleDelete(params.row);
								}}
								disabled={!canDelete || saving}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
			),
		},
	];
	const customButtons = [
		...(canCreate ? [{
			label: "Create Banner",
			icon: <AddIcon />,
			onClick: handleCreate,
			disabled: saving,
		}] : []),
		{
			label: "Refresh Banner",
			icon: <RefreshIcon />,
			onClick: loadBanners,
			disabled: gridLoading || saving,
		},
	];

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box ref={containerRef} sx={{ width: "100%", display: "flex", flexDirection: "column", rowGap: 2 }}>
				{error && <Alert severity="error">{error}</Alert>}

				{gridLoading ? (
					<DatagridLoading actionButtons={2} columns={10} rows={5} />
				) : (
					<Datagrid
						data={visibleBanners}
						columns={columns}
						rowIdField="id"
						customButtons={customButtons}
						showSearchBar
						pageSize={5}
						pageSizeOptions={[5, 10, 25, 50]}
						rowClick
						onRowClick={(params) => openDetail(params.row)}
						checkboxSelection={false}
						autoHeight
						getRowHeight={() => "auto"}
						gridSx={BANNER_GRID_SX}
					/>
				)}

				{showForm && (
					<Paper elevation={1} ref={formRef} sx={{ p: 3, borderRadius: 2, mt: 2, scrollMarginTop: { xs: "96px", md: "148px" } }}>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
							<Box>
								<Typography variant="h6">
									{editingId ? "Edit Banner" : "Create Banner"}
								</Typography>
								<Typography variant="body1" color="text.secondary">
									Preview and configure the notice before it appears in the portal.
								</Typography>
							</Box>
							<Button variant="outlined" onClick={resetForm} disabled={saving}>
								Cancel
							</Button>
						</Box>
						<Grid container spacing={2}>
							<Grid item xs={12} md={5}>
								<TextField
									label="Title"
									value={form.title}
									onChange={(event) => updateForm("title", event.target.value)}
									fullWidth
									size="small"
									disabled={formDisabled}
									error={!!fieldErrors.title}
									helperText={
										fieldErrors.title ||
										`${String(form.title || "").length}/${MAX_BANNER_TITLE_LENGTH}`
									}
									inputProps={{
										maxLength: MAX_BANNER_TITLE_LENGTH,
									}}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<Autocomplete
									multiple
									size="small"
									disableCloseOnSelect
									disabled={formDisabled}
									options={legalEntityOptions.map(toLegalEntityOption)}
									value={form.legal_entities.map(toLegalEntityOption)}
									onChange={handleLegalEntitiesChange}
									inputValue={legalEntityInput}
									onInputChange={(_event, value, reason) => {
										if (reason !== "reset") setLegalEntityInput(value);
									}}
									isOptionEqualToValue={(option, value) => option?.value === value?.value}
									getOptionLabel={(option) => option?.label || ""}
									renderTags={(selected, getTagProps) => {
										if (selected.some((option) => option?.value === "ALL")) {
											return (
												<Chip
													size="small"
													label="All Legal Entities"
													{...getTagProps({ index: 0 })}
												/>
											);
										}

										const visibleSelected = selected.slice(0, 2);
										const hiddenCount = selected.length - visibleSelected.length;

										return (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 0.5,
													minWidth: 0,
													maxWidth: "100%",
												}}
											>
												{visibleSelected.map((option, index) => (
													<Chip
														key={option?.value || index}
														size="small"
														label={option?.label || option?.value}
														{...getTagProps({ index })}
														sx={{
															maxWidth: 72,
															"& .MuiChip-label": {
																overflow: "hidden",
																textOverflow: "ellipsis",
															},
														}}
													/>
												))}

												{hiddenCount > 0 && (
													<Tooltip title={getLegalEntitySelectionLabel(selected)} arrow>
														<Chip
															size="small"
															label={`+${hiddenCount} more`}
															sx={{ cursor: "default" }}
														/>
													</Tooltip>
												)}
											</Box>
										);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Legal Entity"
											placeholder={
												Array.isArray(form.legal_entities) && form.legal_entities.length > 0
													? ""
													: "Select Legal Entity"
											}
											error={!!fieldErrors.legal_entities}
											helperText={fieldErrors.legal_entities || ""}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12} md={2}>
								<FormControl fullWidth size="small" disabled={formDisabled}>
									<InputLabel>Severity</InputLabel>
									<Select
										label="Severity"
										value={form.severity}
										onChange={(event) => updateForm("severity", event.target.value)}
										renderValue={(selected) => renderSeverityOption(selected)}
									>
										{SEVERITY_OPTIONS.map((severity) => (
											<MenuItem key={severity} value={severity}>
												{renderSeverityOption(severity)}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label="Message"
									value={form.message}
									onChange={(event) => updateForm("message", event.target.value)}
									fullWidth
									multiline
									minRows={3}
									size="small"
									disabled={formDisabled}
									error={!!fieldErrors.message}
									helperText={fieldErrors.message}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<DatePicker
									label="Start Date"
									format="MM-DD-YYYY"
									value={toDayjsDate(form.start_date)}
									onChange={(value) => updateForm("start_date", value?.isValid() ? value.format("YYYY-MM-DD") : "")}
									minDate={dayjs(bannerMinDate)}
									disablePast
									slotProps={{
										textField: {
											fullWidth: true,
											size: "small",
											required: true,
											error: !!fieldErrors.start_date,
											helperText: fieldErrors.start_date || " ",
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<DatePicker
									label="End Date"
									format="MM-DD-YYYY"
									value={toDayjsDate(form.end_date)}
									onChange={(value) => updateForm("end_date", value?.isValid() ? value.format("YYYY-MM-DD") : "")}
									minDate={dayjs(form.start_date || bannerMinDate)}
									disablePast
									slotProps={{
										textField: {
											fullWidth: true,
											size: "small",
											required: true,
											error: !!fieldErrors.end_date,
											helperText: fieldErrors.end_date || " ",
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<TextField
									label="CTA Label"
									value={form.cta_label}
									onChange={(event) => updateForm("cta_label", event.target.value)}
									fullWidth
									size="small"
									disabled={formDisabled}
									error={!!fieldErrors.cta_label}
									helperText={
										fieldErrors.cta_label ||
										`${String(form.cta_label || "").length}/${MAX_BANNER_CTA_LABEL_LENGTH}`
									}
									inputProps={{
										maxLength: MAX_BANNER_CTA_LABEL_LENGTH,
									}}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<TextField
									label="CTA URL"
									value={form.cta_url}
									onChange={(event) => updateForm("cta_url", event.target.value)}
									fullWidth
									size="small"
									disabled={formDisabled}
									error={!!fieldErrors.cta_url}
									helperText={fieldErrors.cta_url || " "}
									InputProps={{
										sx: {
											"& input": {
												color: "#0b5cad",
												textDecoration: "underline",
												textUnderlineOffset: "3px",
											},
											"&:hover input": {
												color: "#063f7c",
											},
											"&.Mui-focused input": {
												color: "#063f7c",
											},
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<FormControlLabel
									control={<Switch checked={form.is_active} onChange={(event) => updateForm("is_active", event.target.checked)} disabled={formDisabled} />}
									label="Active"
								/>
								<FormControlLabel
									control={<Switch checked={form.dismissible} onChange={(event) => updateForm("dismissible", event.target.checked)} disabled={formDisabled} />}
									label="Dismissible"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, gap: 1 }}>
									<Button
										variant="contained"
										startIcon={editingId ? <SaveIcon /> : <AddIcon />}
										onClick={handleSubmit}
										disabled={formDisabled}
									>
										{saving ? "Saving..." : editingId ? "Update Banner" : "Create Banner"}
									</Button>
								</Box>
							</Grid>
						</Grid>
					</Paper>
				)}
				{detailBanner && (
					<ModalWrapper
						open={!!detailBanner}
						setOpen={(open) => {
							if (!open) closeDetail();
						}}
						onClose={closeDetail}
						title="Banner Details"
						subtitle="Review banner content, visibility, and status."
						width="60vw"
						content={
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 3,
									maxHeight: "calc(80vh - 220px)",
									overflowY: "auto",
									pr: 1,
								}}
							>
								<Box>
									<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
										Title
									</Typography>
									<Box
										sx={{
											p: 2,
											borderRadius: 1.5,
											bgcolor: "#f3f4f6",
											border: "1px solid #e5e7eb",
										}}
									>
										<Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
											{detailBanner.title
												? (detailTitleExpanded
													? detailBanner.title
													: getCollapsedDetailTitle(detailBanner.title))
												: "N/A"}
											{detailBanner.title && shouldCollapseDetailTitle(detailBanner.title) && (
												<Button
													size="small"
													onClick={() => setDetailTitleExpanded((prev) => !prev)}
													sx={{
														minWidth: 0,
														ml: 0.5,
														p: 0,
														verticalAlign: "baseline",
														textTransform: "none",
														fontSize: "inherit",
														fontWeight: 700,
														lineHeight: "inherit",
														textDecoration: "underline",
														color: "text.primary",
														"&:hover": {
															backgroundColor: "transparent",
															textDecoration: "underline",
														},
													}}
												>
													{detailTitleExpanded ? "Show less" : "View more"}
												</Button>
											)}
										</Typography>
									</Box>
								</Box>
								<Box>
									<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
										Message
									</Typography>
									<Box
										sx={{
											p: 2,
											borderRadius: 1.5,
											bgcolor: "#f3f4f6",
											border: "1px solid #e5e7eb",
										}}
									>
										<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
											{detailBanner.message
												? (detailMessageExpanded
													? detailBanner.message
													: getCollapsedDetailMessage(detailBanner.message))
												: "N/A"}
										</Typography>
										{shouldCollapseDetailMessage(detailBanner.message) && (
											<Button
												size="small"
												onClick={() => setDetailMessageExpanded((prev) => !prev)}
												sx={{
													mt: 1,
													px: 0,
													minWidth: 0,
													textTransform: "none",
													fontWeight: 700,
													color: "text.primary",
													textDecoration: "underline",
												}}
											>
												{detailMessageExpanded ? "Show less" : "View more"}
											</Button>
										)}
									</Box>
								</Box>
								<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
									<Box>
										<Typography variant="caption" color="text.secondary">Legal Entity</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
											{(detailBanner.legal_entities || ["ALL"]).map((entity) => (
												<Chip key={entity} label={getLegalEntityLabel(entity)} size="small" />
											))}
										</Box>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Severity</Typography>
										<Box sx={{ mt: 0.75 }}>{renderSeverityOption(detailBanner.severity || "info")}</Box>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Start Date</Typography>
										<Typography variant="body2" fontWeight={500}>{formatDateDisplay(detailBanner.start_date)}</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">End Date</Typography>
										<Typography variant="body2" fontWeight={500}>{formatDateDisplay(detailBanner.end_date)}</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Status</Typography>
										<Box sx={{ mt: 0.75 }}>
											<Chip
												label={detailBanner.is_active === false ? "Inactive" : "Active"}
												size="small"
												color={detailBanner.is_active === false ? "default" : "success"}
											/>
										</Box>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Dismissible</Typography>
										<Box sx={{ mt: 0.75 }}>
											<Chip
												label={detailBanner.dismissible === false ? "No" : "Yes"}
												size="small"
												color={detailBanner.dismissible === false ? "default" : "success"}
											/>
										</Box>
									</Box>
									{detailBanner.cta_label ? (
										<Box>
											<Typography variant="caption" color="text.secondary">CTA Label</Typography>
											<Typography variant="body2" fontWeight={500}>{detailBanner.cta_label}</Typography>
										</Box>
									) : null}
									{detailBanner.cta_url ? (
										<Box>
											<Typography variant="caption" color="text.secondary">CTA URL</Typography>
											<Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>{detailBanner.cta_url}</Typography>
										</Box>
									) : null}
									<Box>
										<Typography variant="caption" color="text.secondary">Last Modified On</Typography>
										<Typography variant="body2" fontWeight={500}>{formatDateTime(detailBanner.updated_at) || "-"}</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Last Modified By</Typography>
										<Box sx={{ mt: 0.5 }}>
											<UserName size={24} name={detailBanner.updated_by_display_name || "N/A"} />
										</Box>
									</Box>
								</Box>
							</Box>
						}
						actions={
							<Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, width: "100%", gap: 2 }}>
								<Button variant="text" onClick={closeDetail}>
									Close
								</Button>
								<Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 1 }}>
									{canDelete && (
										<Button
											color="error"
											variant="contained"
											startIcon={<DeleteIcon />}
											disabled={saving}
											onClick={() => handleDelete(detailBanner)}
										>
											Delete
										</Button>
									)}
									{canEdit && (
										<Button
											variant="contained"
											startIcon={<EditIcon />}
											disabled={saving}
											onClick={() => {
												handleEdit(detailBanner);
												closeDetail();
											}}
										>
											Edit Banner
										</Button>
									)}
								</Box>
							</Box>
						}
					/>
				)}
			</Box>
		</LocalizationProvider>
	);
};

export default ManageBanners;
